import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import {
  Table, Modal, Form, InputNumber, Select, Switch, message,
  Popconfirm, Button, Space, Tabs, Input, Tag, Alert,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getQuestionList, getOptionSelect,
  getProCourseCondList, createProCourseCond, deleteProCourseCond,
  getProCourseComboList, createProCourseCombo, updateProCourseCombo, deleteProCourseCombo, toggleProCourseComboUse,
  getSolutionGroupSelect,
} from "../api/domainApi";
import { ProCourseCondRes, ProCourseComboRes } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import UseYnTag from "../components/common/UseYnTag";

const ProCoursePage: React.FC = () => {
  const { qstId: qstIdParam } = useParams<{ qstId: string }>();
  const navigate = useNavigate();
  const qstId = qstIdParam ? Number(qstIdParam) : null;

  // 조건은 백엔드에 PUT 이 없음(삭제→재등록 설계) — 등록 전용 모달
  const [condModalOpen, setCondModalOpen] = useState(false);
  const [condForm] = Form.useForm();
  const refQstId = Form.useWatch("refQstId", condForm);

  const [comboModalOpen, setComboModalOpen] = useState(false);
  const [editingCombo, setEditingCombo] = useState<ProCourseComboRes | null>(null);
  const [comboForm] = Form.useForm();

  const { data: questionPage } = useSWR("/question/all-for-select", () =>
    getQuestionList({ size: 1000, page: 0 })
  );

  const { data: sltnGroups } = useSWR("/solution-group/select", getSolutionGroupSelect);

  // 조건 모달에서 선택한 참조문항의 선택지 목록
  const { data: refOptions } = useSWR(
    refQstId ? `/question-option/select?questionId=${refQstId}` : null,
    () => getOptionSelect({ questionId: refQstId as number })
  );

  const { data: conds, mutate: mutateCond } = useSWR(
    qstId ? `/pro-course/${qstId}/cond` : null,
    () => getProCourseCondList(qstId!)
  );

  const { data: combos, mutate: mutateCombo } = useSWR(
    qstId ? `/pro-course/${qstId}/combo` : null,
    () => getProCourseComboList(qstId!)
  );

  const openCondCreate = () => {
    condForm.resetFields();
    condForm.setFieldsValue({ condLogic: "OR" });
    setCondModalOpen(true);
  };

  const handleCondOk = async () => {
    const values = await condForm.validateFields();
    try {
      await createProCourseCond(qstId!, values);
      message.success("추가되었습니다.");
      setCondModalOpen(false);
      mutateCond();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleCondDelete = async (condId: number) => {
    try {
      await deleteProCourseCond(qstId!, condId);
      message.success("삭제되었습니다.");
      mutateCond();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const openComboCreate = () => {
    setEditingCombo(null);
    comboForm.resetFields();
    setComboModalOpen(true);
  };
  const openComboEdit = (row: ProCourseComboRes) => {
    setEditingCombo(row);
    comboForm.setFieldsValue({
      cond1: row.cond1, cond2: row.cond2, cond3: row.cond3, cond4: row.cond4,
      tierNo: row.tierNo, sortOrder: row.sortOrder,
      diagnosisName: row.diagnosisName, diagnosisDescription: row.diagnosisDescription,
      sltnGrpId: row.sltnGrpId,
    });
    setComboModalOpen(true);
  };

  const handleComboOk = async () => {
    const values = await comboForm.validateFields();
    try {
      if (editingCombo) {
        await updateProCourseCombo(qstId!, editingCombo.id, values);
        message.success("수정되었습니다.");
      } else {
        await createProCourseCombo(qstId!, values);
        message.success("추가되었습니다.");
      }
      setComboModalOpen(false);
      mutateCombo();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleComboDelete = async (id: number) => {
    try {
      await deleteProCourseCombo(qstId!, id);
      message.success("삭제되었습니다.");
      mutateCombo();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const condColumns: ColumnsType<ProCourseCondRes> = [
    { title: "ID", dataIndex: "id", width: 70 },
    { title: "조건No", dataIndex: "condNo", width: 80 },
    { title: "로직", dataIndex: "condLogic", width: 70 },
    { title: "참조문항 ID", dataIndex: "refQstId", width: 100 },
    { title: "참조문항", dataIndex: "refQstTitle", ellipsis: { showTitle: true } },
    { title: "지표", dataIndex: "refIndicatorName", width: 100 },
    {
      title: "트리거 선택지", dataIndex: "triggerOptions", width: 200, ellipsis: { showTitle: true },
      render: (_, row) => (
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.triggerOptions?.map((o) => (
            <Tag key={o.qstOptId} style={{ marginBottom: 0 }}>{o.content}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: "관리", width: 90,
      render: (_, row) => (
        <Popconfirm title="삭제?" onConfirm={() => handleCondDelete(row.id)}>
          <Button size="small" danger>삭제</Button>
        </Popconfirm>
      ),
    },
  ];

  const comboColumns: ColumnsType<ProCourseComboRes> = [
    { title: "ID", dataIndex: "id", width: 60 },
    { title: "조합", dataIndex: "comboLabel", width: 90 },
    { title: "티어", dataIndex: "tierNo", width: 60 },
    { title: "정렬", dataIndex: "sortOrder", width: 60 },
    { title: "진단명", dataIndex: "diagnosisName", ellipsis: { showTitle: true } },
    { title: "솔루션그룹", dataIndex: "sltnGrpTitle", width: 120 },
    { title: "사용여부", dataIndex: "useYn", width: 80, render: (v) => <UseYnTag value={v} /> },
    {
      title: "관리", width: 160,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openComboEdit(row)}>수정</Button>
          <Button size="small" onClick={async () => {
            try { await toggleProCourseComboUse(qstId!, row.id); mutateCombo(); }
            catch (e: any) { message.error(e.message); }
          }}>
            {row.useYn ? "비활성" : "활성"}
          </Button>
          <Popconfirm title="삭제?" onConfirm={() => handleComboDelete(row.id)}>
            <Button size="small" danger>삭제</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const questionOptions = questionPage?.content?.map((q) => ({
    value: q.id,
    label: `#${q.id} ${q.title}`,
  })) ?? [];

  return (
    <div>
      <PageHeader title="프로코스 설정" />

      <div style={{ marginBottom: 20 }}>
        <Select
          style={{ width: 480 }}
          placeholder="문항을 선택하세요"
          value={qstId ?? undefined}
          onChange={(v) => navigate(`/pro-courses/${v}`)}
          options={questionOptions}
          showSearch={{ filterOption: (input, option) =>
            String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }}
          allowClear
          onClear={() => navigate("/pro-courses")}
        />
      </div>

      {qstId && (
        <Tabs
          items={[
            {
              key: "cond",
              label: "조건",
              children: (
                <>
                  <div style={{ textAlign: "right", marginBottom: 12 }}>
                    <Button type="primary" onClick={openCondCreate}>+ 조건 추가</Button>
                  </div>
                  <Table
                    rowKey="id"
                    columns={condColumns}
                    dataSource={conds}
                    pagination={false}
                    size="small"
                  />
                </>
              ),
            },
            {
              key: "combo",
              label: "조합",
              children: (
                <>
                  <div style={{ textAlign: "right", marginBottom: 12 }}>
                    <Button type="primary" onClick={openComboCreate}>+ 조합 추가</Button>
                  </div>
                  <Table
                    rowKey="id"
                    columns={comboColumns}
                    dataSource={combos}
                    pagination={false}
                    size="small"
                  />
                </>
              ),
            },
          ]}
        />
      )}

      {/* Cond Modal — 등록 전용 (수정은 삭제 후 재등록) */}
      <Modal
        title="조건 추가"
        open={condModalOpen}
        onOk={handleCondOk}
        onCancel={() => setCondModalOpen(false)}
        okText="추가"
        cancelText="취소"
      >
        <Alert
          type="info"
          message="조건은 수정할 수 없습니다. 변경하려면 삭제 후 다시 등록하세요."
          style={{ marginBottom: 16 }}
        />
        <Form form={condForm} layout="vertical">
          <Form.Item name="condNo" label="조건 번호 (1~4)" rules={[{ required: true }]}>
            <InputNumber min={1} max={4} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="condLogic" label="조건 로직" rules={[{ required: true }]}>
            <Select options={[{ value: "OR", label: "OR" }, { value: "AND", label: "AND" }]} />
          </Form.Item>
          <Form.Item name="refQstId" label="참조 문항" rules={[{ required: true }]}>
            <Select
              placeholder="참조할 문항을 선택하세요"
              options={questionOptions}
              showSearch={{ filterOption: (input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }}
              onChange={() => condForm.setFieldsValue({ qstOptIds: [] })}
            />
          </Form.Item>
          <Form.Item
            name="qstOptIds"
            label="트리거 선택지 (다중 선택)"
            rules={[{ required: true, message: "트리거 선택지를 1개 이상 선택하세요." }]}
          >
            <Select
              mode="multiple"
              placeholder={refQstId ? "트리거가 될 선택지를 선택하세요" : "먼저 참조 문항을 선택하세요"}
              disabled={!refQstId}
              options={refOptions?.map((o) => ({
                value: o.id,
                label: o.content,
              }))}
              showSearch={{ filterOption: (input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Combo Modal */}
      <Modal
        title={editingCombo ? "조합 수정" : "조합 추가"}
        open={comboModalOpen}
        onOk={handleComboOk}
        onCancel={() => setComboModalOpen(false)}
        okText={editingCombo ? "수정" : "추가"}
        cancelText="취소"
      >
        <Form form={comboForm} layout="vertical" style={{ marginTop: 16 }}>
          <Space wrap>
            <Form.Item name="cond1" label="조건1" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="cond2" label="조건2" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="cond3" label="조건3" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="cond4" label="조건4" valuePropName="checked"><Switch /></Form.Item>
          </Space>
          <Form.Item name="tierNo" label="결과 티어 (1~4)" rules={[{ required: true }]}>
            <InputNumber min={1} max={4} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="sortOrder" label="정렬 순서 (같은 티어 내, 낮을수록 우선 / 생략 시 0)">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="diagnosisName" label="진단명 / 증상명" rules={[{ required: true }]}>
            <Input placeholder="예: 통제 불능 뇌 과부하" />
          </Form.Item>
          <Form.Item name="diagnosisDescription" label="진단 상세 설명 (선택)">
            <Input.TextArea rows={3} placeholder="진단에 대한 상세 설명문" />
          </Form.Item>
          <Form.Item name="sltnGrpId" label="솔루션 그룹" rules={[{ required: true }]}>
            <Select
              options={sltnGroups?.map((g) => ({ value: g.id, label: g.sltnGrpTitle }))}
              showSearch={{ filterOption: (input, option) =>
                String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProCoursePage;
